import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    localStorage.setItem('token', access_token);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    localStorage.setItem('token', access_token);
    setUser(userData);
    return userData;
  }, []);

  const requestMagicLink = useCallback(async (email) => {
    await axios.post(`${API}/auth/magic-link`, { email });
  }, []);

  const verifyMagicLink = useCallback(async (magicToken) => {
    const response = await axios.post(`${API}/auth/magic-link/verify`, { token: magicToken });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    localStorage.setItem('token', access_token);
    setUser(userData);
    return userData;
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin + '/app/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const processGoogleSession = useCallback(async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/session`, 
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data.user);
    return response.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error.message);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  }, []);

  const updateProfile = useCallback(async (data) => {
    const response = await axios.put(`${API}/users/profile`, data);
    setUser(response.data);
    return response.data;
  }, []);

  const isAdmin = user?.role && ['super_admin', 'fund_director', 'analyst', 'compliance'].includes(user.role);
  const isInvestor = user?.role === 'investor';
  const isPropertyContributor = user?.role === 'property_contributor';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      isAuthenticated: !!user,
      isAdmin,
      isInvestor,
      isPropertyContributor,
      login,
      register,
      logout,
      requestMagicLink,
      verifyMagicLink,
      loginWithGoogle,
      processGoogleSession,
      updateProfile,
      checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
